import { FC, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Modal from 'components/layouts/modal';
import { StepWizard } from 'components';
import useApi from 'utils/use-api';
import { useAuthStore } from 'state/auth';
import wrapClick from 'utils/wrap-click';
import RequestDetailsStep from 'components/forms/access-request/request-details-step';
import AssigneeStep from 'components/forms/access-request/assignee-step';
import ReviewStep from 'components/forms/access-request/review-step';
import toast from 'react-hot-toast';

interface CreateRequestModalProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  onSuccess: () => void;
}

const validationSchema = Yup.object({
  category: Yup.string().required('Request category is required'),
  details: Yup.string().required('Request details are required').min(10, 'Details must be at least 10 characters'),
  assigneeType: Yup.string().oneOf(['group', 'user'], 'Invalid assignee type').required('Assignee type is required'),
  assigneeId: Yup.number().required('Assignee is required'),
  assigneeName: Yup.string().required('Assignee name is required'),
});

const CreateRequestModal: FC<CreateRequestModalProps> = ({ open, setOpen, onSuccess }) => {
  const api = useApi();
  const currentUser = useAuthStore(state => state.user);
  const [currentStep, setCurrentStep] = useState(0);
  const [lastStep, setLastStep] = useState(0);

  const steps = [
    { name: 'Request Details', href: 0 },
    { name: 'Assign To', href: 1 },
    { name: 'Review', href: 2 },
  ];

  const form = useFormik({
    initialValues: {
      category: '',
      details: '',
      assigneeType: 'group' as 'group' | 'user',
      assigneeId: 0,
      assigneeName: '',
    },
    validationSchema,
    onSubmit: async values => {
      try {
        await createRequestMutation.mutateAsync(values);
        toast(JSON.stringify({ type: 'success', title: 'Request created' }));
      } catch (err: any) {
        toast(JSON.stringify({ type: 'error', title: err?.response?.data?.message || 'Unable to create request' }));
      }
    },
  });

  const createRequestMutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      const response = await api.post('/access-requests', {
        requesterName: currentUser?.fullName || `${currentUser?.firstName} ${currentUser?.lastName}`,
        requesterEmail: currentUser?.email,
        category: values.category,
        details: values.details,
        assigneeType: values.assigneeType,
        assigneeId: Number(values.assigneeId),
        assigneeName: values.assigneeName,
      });
      return response.data;
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.response ||
        (error?.message === 'Network Error' ? 'Network connection lost. Connect and try again' : 'Unable to process request');
      toast(JSON.stringify({ type: 'error', title: message }));
    },
    onSuccess: () => {
      form.resetForm();
      setCurrentStep(0);
      setLastStep(0);
      onSuccess();
    },
  });

  const handleNext = () => {
    if (currentStep === 0) {
      if (!form.values.category || !form.values.details) {
        form.setTouched({ category: true, details: true });
        return;
      }
    } else if (currentStep === 1) {
      if (!form.values.assigneeType || !form.values.assigneeId || !form.values.assigneeName) {
        form.setTouched({ assigneeType: true, assigneeId: true, assigneeName: true });
        return;
      }
    }

    setCurrentStep(currentStep + 1);
    setLastStep(Math.max(lastStep, currentStep + 1));
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step: number) => {
    if (step <= lastStep) {
      setCurrentStep(step);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <RequestDetailsStep form={form} />;
      case 1:
        return <AssigneeStep form={form} />;
      case 2:
        return <ReviewStep form={form} />;
      default:
        return null;
    }
  };

  const handleSubmitFinal = async () => {
    const errors = await form.validateForm();
    if (Object.keys(errors).length) {
      form.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}) as any,
        true,
      );
      toast(JSON.stringify({ type: 'error', title: 'Please fix the highlighted fields' }));
      return;
    }
    await form.submitForm();
  };

  const renderActions = () => {
    return (
      <div className="flex gap-x-3">
        {currentStep > 0 && (
          <button
            type="button"
            onClick={wrapClick(handlePrevious)}
            className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
          >
            Previous
          </button>
        )}
        {currentStep < steps.length - 1 ? (
          <button
            type="button"
            onClick={wrapClick(handleNext)}
            className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            onClick={wrapClick(handleSubmitFinal)}
            disabled={createRequestMutation.isPending}
            className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createRequestMutation.isPending ? 'Submitting...' : 'Submit Request'}
          </button>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Create New Request"
      description="Fill in the details to create a new access request"
      size="5xl"
      renderActions={renderActions}
    >
      <div className="space-y-6">
        <StepWizard steps={steps} step={currentStep} setStep={handleStepClick} lastStep={lastStep} />
        <div className="mt-6">{renderStepContent()}</div>
      </div>
    </Modal>
  );
};

export default CreateRequestModal;
